/**
 * AddReceiptCxP.js — Formulario pago a proveedor
 * UI idéntico a AddReceipt: receipt-container (1fr 1fr), receipt-details (izq),
 * cxc-details (der) ahora = "Facturas Pendientes", receipt-footer (bottom).
 * CSS: addReceipt.module.css (mismas clases que AddReceipt).
 *
 * Paridad con CxC (AddReceipt.js / ReceiptDetails.js / CxcDetails.js /
 * InvoiceCxc.js / useCxc.js):
 *   · La moneda del recibo se DERIVA del banco elegido (no hay toggle manual)
 *     — solo el TC es editable, igual que ReceiptDetails.js.
 *   · Diferencial cambiario y comisión bancaria son POR FACTURA (botón en
 *     cada fila), igual que InvoiceCxc.js — no un campo/checkbox global.
 *   · Botón "Limpiar Formulario" y "Acumular Saldos a Favor" (fusionar ≥2
 *     créditos), igual que ReceiptFooter.js / CxcDetails.js.
 *   · Pago 100% con saldo a favor (monto=0): referencia bancaria autogenerada
 *     y deshabilitada, igual que soloSaldoFavor en useCxc.js/ReceiptDetails.js.
 *   · Resumen muestra los totales en la moneda del recibo (no siempre ₡) y,
 *     si hay facturas en otra moneda, la línea "incluye ... al TC actual",
 *     igual que CxcSummary.js.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../receipts/styles/addReceipt.module.css";
import { useTipoCambio } from "../../services/useTipoCambio";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import PagoSinIdentificarFormCxP from "../components/PagoSinIdentificarFormCxP";
import AcumularSaldosDialogCxP from "../components/AcumularSaldosDialogCxP";
import { SearchSelect } from "../../../../shared/components/SearchSelect";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { parseMoney, sanitizeMoneyTyping } from "../../../../shared/utils/parseMoney";
import DecimalInput from "../../../../shared/components/DecimalInput";

/** Formatea un valor USD mostrando hasta 4 decimales, pero nunca menos de 2 (igual que InvoiceCxc.js) */
function fmtUSD(n) {
  const s = Number(n).toFixed(4);
  return s.replace(/(\.\d{2})0+$/, "$1");
}

/** Input de Abono con formato de miles al perder foco — espejo de InvoiceCxc.js */
function AbonoInput({ value, onChange, disabled, esUSD, className }) {
  const [displayValue, setDisplayValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      if (!value) setDisplayValue("");
      else setDisplayValue(esUSD ? fmtUSD(value) : formatCurrency(value));
    }
  }, [value, isEditing, esUSD]);

  return (
    <input type="text" className={className}
      value={displayValue}
      disabled={disabled}
      placeholder={esUSD ? "0.0000" : "0.00"}
      onFocus={() => {
        setIsEditing(true);
        setDisplayValue(value ? (esUSD ? fmtUSD(value) : value.toString()) : "");
      }}
      onChange={(e) => {
        const raw = sanitizeMoneyTyping(e.target.value);
        const num = parseMoney(raw);
        setDisplayValue(raw);
        onChange(num);
      }}
      onBlur={() => {
        setIsEditing(false);
        if (!value) setDisplayValue("");
        else setDisplayValue(esUSD ? fmtUSD(value) : formatCurrency(value));
      }}
    />
  );
}

const r2  = (n) => Math.round(Number(n) * 100) / 100;
const r4  = (n) => Math.round(Number(n) * 10000) / 10000;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

/** "2026-04-15" -> "15/04/2026" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

const PAGE_SIZE = 10;
const FORM_INIT = {
  fecha_pago:      new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0],
  monto:           "",
  metodo_pago:     "TRANSFERENCIA",
  documento:       "",
  descripcion:     "",
  cuenta_bancaria: null,
  cuenta_proveedor: null,
  cuenta_proveedor_nombre: "",
  moneda_pago:     "CRC",
  tipo_cambio:     1,
};

/**
 * Facturas del proveedor con saldo distinto de 0 (incluye saldos a favor,
 * saldo < 0), ordenadas con los saldos a favor primero.
 */
function pickFacturas(all, proveedorId) {
  return all
    .filter((cxp) =>
      cxp.factura_cxp?.cuenta_proveedor?.id === proveedorId &&
      r2(Number(cxp.saldo)) !== 0)
    .sort((a, b) => {
      const na = Number(a.saldo) < 0, nb = Number(b.saldo) < 0;
      if (na && !nb) return -1;
      if (!na && nb) return 1;
      return 0;
    });
}

export default function AddReceiptCxP() {
  const { t } = useTranslation();

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({ ...FORM_INIT });
  const { tipoCambio, loading: loadingTC } = useTipoCambio();
  const multimoneda = useMultimoneda();

  // Auto-populate TC from BCCR (tipo_compra) — solo cuando el TC es 1 (valor inicial)
  useEffect(() => {
    if (tipoCambio?.tipo_compra && (!form.tipo_cambio || form.tipo_cambio === 1)) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, form.tipo_cambio]);

  // ── Data ──────────────────────────────────────────────────────────────────
  // facturasCxP: [{ ...cxp, abono, favor, checked, comision_bancaria, tratar_como_diferencial }]
  // abono en moneda del recibo
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentasProveedor, setCuentasProveedor] = useState([]);
  const [facturasCxP,      setFacturasCxP]      = useState([]);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [apiErr,     setApiErr]     = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [favorMsg,   setFavorMsg]   = useState("");
  const [loadingFac, setLoadingFac] = useState(false);
  const [page,       setPage]       = useState(1);
  const [fusionOpen, setFusionOpen] = useState(false);

  // ── Pago sin identificar (manual) ──────────────────────────────────────
  const [sinIdentificarMode, setSinIdentificarMode] = useState(false);
  const [sinIdentificarForm, setSinIdentificarForm] = useState({
    monto: "", fecha_pago: "", metodo_pago: "TRANSFERENCIA",
    documento: "", descripcion: "", cuenta_bancaria: "",
    tipo_cambio: "", moneda_pago: "CRC", cuenta_proveedor: "",
  });
  const [sinIdentificarError, setSinIdentificarError] = useState("");
  const [sinIdentificarSaving, setSinIdentificarSaving] = useState(false);

  // ── Load banks and proveedores ─────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = d.results ?? d ?? [];
        setCuentasBancarias(list);
        if (list.length > 0) {
          const first = list[0];
          setForm(p => ({
            ...p,
            cuenta_bancaria: first.id,
            moneda_pago: (first.moneda || 'CRC').toUpperCase(),
          }));
        }
      });
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1&bajo_relacion=proveedores&page_size=500")
      .then(r => r.json())
      .then(d => setCuentasProveedor(Array.isArray(d) ? d : (d.results ?? [])));
  }, []);

  function reloadFacturas() {
    if (!form.cuenta_proveedor) { setFacturasCxP([]); return; }
    setLoadingFac(true);
    apiFetch(`/api/finance/cuentaXPagar/?page_size=9999`)
      .then(r => r.json())
      .then(d => {
        const all = d.results ?? [];
        const mine = pickFacturas(all, form.cuenta_proveedor)
          .map(cxp => ({ ...cxp, abono: 0, favor: 0, checked: false, comision_bancaria: 0, tratar_como_diferencial: false }));
        setFacturasCxP(mine);
        setPage(1);
      })
      .finally(() => setLoadingFac(false));
  }

  // ── Load facturas when proveedor changes ───────────────────────────────────
  useEffect(() => {
    reloadFacturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cuenta_proveedor]);

  // ── Reset de abonos al cambiar el monto del recibo ─────────────────────────
  // Igual que el efecto sobre receiptMonto en useCxc.js: si cambia el
  // presupuesto, los abonos calculados con el monto anterior dejan de ser
  // válidos (el restante quedaría descuadrado y bloquearía el guardado).
  useEffect(() => {
    setFacturasCxP(prev => prev.map(f => ({
      ...f, abono: 0, favor: 0, checked: false,
      comision_bancaria: 0, tratar_como_diferencial: false,
    })));
    setFavorMsg("");
  }, [form.monto]);

  // Costo (en moneda del recibo) para saldar el saldo de una factura.
  // Para saldos a favor (saldo < 0) devuelve un valor NEGATIVO = crédito.
  function costInRecibo(f) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    if (facMon === form.moneda_pago) return Number(f.saldo);
    if (form.moneda_pago === "CRC") {
      return Number(f.saldo) < 0
        ? Number(f.saldo) * (Number(f.tipo_cambio) || 1)
        : Number(f.saldo_colones || 0);
    }
    return Number(f.saldo) / (Number(form.tipo_cambio) || 1);
  }

  // ── Conversión abono ↔ moneda del recibo ───────────────────────────────
  // El campo "Abono" se muestra y edita SIEMPRE en la moneda ORIGINAL de la
  // factura (igual que InvoiceCxc.js/useCxc.js en CxC — "el abono se adapta
  // a la factura"). El backend (DetalleReciboCxP.monto) sigue esperando el
  // monto en la moneda del RECIBO, así que se convierte aquí, no en el back.
  function abonoEnRecibo(f, amountFacturaCurrency) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    const amt    = Number(amountFacturaCurrency) || 0;
    if (facMon === form.moneda_pago) return r2(amt);
    const saldoInv = r2(Number(f.saldo));
    // Crédito (saldo a favor) aplicado completo → mismo valor que costInRecibo
    // para que el crédito sumado en distribute() y el abono resten idéntico.
    if (saldoInv < 0) {
      if (r2(amt) <= saldoInv + 0.01) return r2(costInRecibo(f));
      return convARecibo(amt);
    }
    // Pago que cubre el saldo completo → la porción del SALDO se valora con
    // costInRecibo (TC propio de la factura / saldo_colones) y el EXCESO se
    // convierte al TC del recibo. Antes se devolvía solo costInRecibo, lo que
    // hacía DESAPARECER el excedente del total y dejaba el restante en el aire
    // (el botón "Registrar Pago" nunca podía guardar).
    if (r2(amt) >= saldoInv - 0.01) {
      // Sin redondear los sumandos: así abonoEnRecibo(facturaDesdeRecibo(x)) === x
      // y el valor coincide con lo que el backend registra (monto × TC).
      return r2(costInRecibo(f) + convARecibo(amt - saldoInv, false));
    }
    return convARecibo(amt);
  }

  /** Convierte un monto en moneda de factura → moneda del recibo, al TC del recibo */
  function convARecibo(amt, redondear = true) {
    const tc  = Number(form.tipo_cambio) || 1;
    const val = form.moneda_pago === "CRC" ? Number(amt) * tc : Number(amt) / tc;
    return redondear ? r2(val) : val;
  }

  function facturaFromRecibo(f, amountReciboCurrency) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    const round  = facMon === "USD" ? r4 : r2;
    if (facMon === form.moneda_pago) return round(amountReciboCurrency);
    return round(form.moneda_pago === "CRC"
      ? Number(amountReciboCurrency) / (Number(form.tipo_cambio) || 1)
      : Number(amountReciboCurrency) * (Number(form.tipo_cambio) || 1));
  }

  /**
   * Inversa EXACTA de abonoEnRecibo(): dado un monto en moneda del recibo,
   * devuelve el abono en moneda de la factura tal que
   * abonoEnRecibo(f, resultado) === montoRecibo (sin residuos de redondeo).
   * Si el monto cubre el saldo completo, la parte del saldo se toma tal cual
   * y solo el exceso se convierte al TC del recibo.
   */
  function facturaDesdeRecibo(f, montoRecibo) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    const round  = facMon === "USD" ? r4 : r2;
    const monto  = Number(montoRecibo) || 0;
    const saldoInv = r2(Number(f.saldo));
    if (facMon !== form.moneda_pago && saldoInv > 0) {
      const cost = costInRecibo(f);           // sin redondear — ver abonoEnRecibo
      if (r2(monto) >= r2(cost) - 0.005) {
        const exceso = monto - cost;
        return round(saldoInv + Number(facturaFromRecibo(f, exceso)));
      }
    }
    return facturaFromRecibo(f, monto);
  }

  /** Costo real en moneda del recibo del abono actual de una factura */
  function costoAbono(f) {
    return Number(abonoEnRecibo(f, f.abono || 0));
  }

  function set(field, val) { setForm(p => ({ ...p, [field]: val })); }

  function handleProveedor(id, obj) {
    setForm(p => ({ ...p, cuenta_proveedor: id, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  // La moneda del recibo se DERIVA del banco elegido — igual que CxC
  // (ReceiptDetails.js). No hay forma de que el usuario la cambie a mano.
  function handleBancoChange(e) {
    const id    = Number(e.target.value) || null;
    const banco = cuentasBancarias.find(b => b.id === id);
    const moneda = (banco?.moneda || 'CRC').toUpperCase();
    setForm(p => ({ ...p, cuenta_bancaria: id, moneda_pago: multimoneda ? moneda : 'CRC' }));
  }

  // ── Marcar/desmarcar una factura (abona hasta lo disponible del presupuesto) ──
  // El abono guardado en el estado está SIEMPRE en la moneda propia de la
  // factura — igual que useCxc.js — el presupuesto del recibo se calcula
  // convirtiendo vía abonoEnRecibo()/facturaFromRecibo().
  function toggleFactura(id) {
    setFavorMsg("");
    const prev   = facturasCxP;
    const target = prev.find((f) => f.id === id);
    if (!target) return;

    const aplicar = (patch) =>
      setFacturasCxP((cur) => cur.map((f) => (f.id === id ? { ...f, ...patch } : f)));

    if (target.checked) {
      aplicar({ abono: 0, favor: 0, checked: false, comision_bancaria: 0, tratar_como_diferencial: false });
      return;
    }

    // Saldo a favor: marcar aplica el crédito completo (abono negativo, en moneda propia)
    if (Number(target.saldo) < 0) {
      aplicar({ abono: Number(target.saldo), favor: 0, checked: true });
      return;
    }

    // Factura pendiente: abonar hasta lo disponible del presupuesto (form.monto, moneda del recibo)
    const costRecibo  = r2(costInRecibo(target));
    const presupuesto = Number(form.monto) || 0;
    // usado ya incluye los créditos marcados (abono negativo → resta)
    const usado = prev.reduce((s, f) => s + (f.checked ? costoAbono(f) : 0), 0);
    const disponibleRecibo = r2(presupuesto - usado);

    let abonoRecibo = r2(Math.min(costRecibo, Math.max(0, disponibleRecibo)));
    let favorRecibo = 0;

    // Auto-saldo a favor — IGUAL que updateChecked() en useCxc.js (CxC):
    // si sobra dinero y ya no queda ninguna otra factura pendiente sin marcar,
    // el excedente se aplica a ESTA factura como saldo a favor.
    const hayOtraSinMarcar = prev.some(
      (f) => f.id !== id && Number(f.saldo) > 0 && !f.checked);
    if (!hayOtraSinMarcar && disponibleRecibo > abonoRecibo + 0.005) {
      favorRecibo = r2(disponibleRecibo - abonoRecibo);
      abonoRecibo = r2(disponibleRecibo);
    }

    const abonoFactura = abonoRecibo > 0 ? facturaDesdeRecibo(target, abonoRecibo) : 0;
    const saldoInv     = r2(Number(target.saldo));
    const esUSD        = (target.moneda || "CRC").toUpperCase() === "USD";
    const favorFactura = favorRecibo > 0
      ? (esUSD ? r4(abonoFactura - saldoInv) : r2(abonoFactura - saldoInv))
      : 0;

    if (favorRecibo > 0) {
      setFavorMsg(t("recibos.alertaFavor", {
        favor: `${form.moneda_pago === "USD" ? "$" : "₡"}${fmt(favorRecibo)}`,
      }));
    }

    aplicar({ abono: abonoFactura, favor: favorFactura, checked: abonoFactura > 0 });
  }

  // ── Editar abono manualmente — SIEMPRE en la moneda propia de la factura
  // (igual que InvoiceCxc.js/useCxc.js). Cualquier cambio manual resetea la
  // comisión (igual que useCxc.js).
  function updateAbono(id, rawValue) {
    const target = facturasCxP.find((f) => f.id === id);
    if (!target) return;
    const esUSD = (target.moneda || "CRC").toUpperCase() === "USD";
    let value = esUSD ? r4(Number(rawValue) || 0) : r2(Number(rawValue) || 0);
    let favor = 0;

    if (Number(target.saldo) < 0) {
      setFacturasCxP((prev) => prev.map((f) =>
        f.id === id ? { ...f, abono: value, favor: 0, comision_bancaria: 0 } : f));
      return;
    }

    // Capar al presupuesto disponible del recibo, convertido a moneda de la factura.
    // El tope NO es el saldo de la factura: el exceso se vuelve saldo a favor
    // (igual que updateAbono() en useCxc.js).
    const presupuesto = Number(form.monto) || 0;
    const usadoOtros = facturasCxP.reduce((s, f) =>
      f.id !== id && f.checked ? s + Number(abonoEnRecibo(f, f.abono || 0)) : s, 0);
    const disponibleRecibo  = r2(Math.max(0, presupuesto - usadoOtros));
    const disponibleFactura = facturaDesdeRecibo(target, disponibleRecibo);
    if (value > disponibleFactura) value = esUSD ? r4(disponibleFactura) : r2(disponibleFactura);

    const saldoInv = r2(Number(target.saldo));
    if (value > saldoInv) {
      favor = esUSD ? r4(value - saldoInv) : r2(value - saldoInv);
      setFavorMsg(t("recibos.alertaFavor", {
        favor: `${esUSD ? "$" : "₡"}${fmt(favor)}`,
      }));
    } else {
      setFavorMsg("");
    }

    setFacturasCxP((prev) => prev.map((f) =>
      f.id === id ? { ...f, abono: value, favor, checked: value !== 0, comision_bancaria: 0 } : f));
  }

  // ── Comisión bancaria por factura (pago con Tarjeta) — igual que
  // toggleComisionBancaria en useCxc.js: la diferencia entre el saldo de la
  // factura y el abono actual (ambos en moneda propia) se registra como
  // comisión que el banco retuvo — convertida a moneda del recibo para el
  // campo comision_bancaria (backend la espera así); la factura queda
  // cancelada por el total aunque el banco reciba menos.
  function toggleComisionBancaria(id) {
    setFacturasCxP((prev) => prev.map((f) => {
      if (f.id !== id) return f;
      const activating = !(Number(f.comision_bancaria) > 0);
      if (!activating) return { ...f, comision_bancaria: 0 };
      const saldoInv    = r2(Number(f.saldo));
      const gapFactura  = r2(saldoInv - Number(f.abono || 0));
      if (gapFactura <= 0) return f;
      const gapRecibo = r2(abonoEnRecibo(f, gapFactura));
      return { ...f, comision_bancaria: gapRecibo, favor: 0, tratar_como_diferencial: false };
    }));
  }

  // ── Diferencial cambiario por factura — igual que toggleDiferencial en
  // useCxc.js: al activar, el exceso sobre el saldo NO se vuelve saldo a
  // favor sino que el asiento lo trata como diferencial cambiario.
  function toggleDiferencial(id) {
    setFacturasCxP((prev) => prev.map((f) => {
      if (f.id !== id) return f;
      if (f.tratar_como_diferencial) return { ...f, tratar_como_diferencial: false };
      const saldoInv = r2(Number(f.saldo));
      const abono = Number(f.abono || 0) > 0 ? Math.min(Number(f.abono), saldoInv) : saldoInv;
      return { ...f, tratar_como_diferencial: true, abono, favor: 0, comision_bancaria: 0 };
    }));
  }

  // ── Distribuir automáticamente el monto total entre las facturas ──────
  function distribute() {
    setFavorMsg("");
    const presupuesto = Number(form.monto) || 0;
    // Crédito de saldos a favor ya marcados (abono negativo)
    const credito = facturasCxP.reduce(
      (s, f) => s + (Number(f.saldo) < 0 && f.checked ? -r2(costInRecibo(f)) : 0), 0);
    let rem = r2(presupuesto + credito);
    if (rem <= 0) {
      setFavorMsg(t("recibos.alertaSinMonto"));
      return;
    }

    const positivos = facturasCxP.filter((f) => Number(f.saldo) > 0);
    const lastPosId = positivos.length ? positivos[positivos.length - 1].id : null;
    let avisoFavor = 0;

    const next = facturasCxP.map((f) => {
      if (Number(f.saldo) < 0) {
        return f.checked
          ? { ...f, abono: Number(f.saldo), favor: 0, checked: true }
          : { ...f, abono: 0, favor: 0, checked: false };
      }
      if (rem <= 0) return { ...f, abono: 0, favor: 0, checked: false, comision_bancaria: 0, tratar_como_diferencial: false };
      const cost = r2(costInRecibo(f));
      let abonoRecibo, favorRecibo = 0;
      if (f.id === lastPosId) {
        abonoRecibo = r2(rem);
        if (abonoRecibo > cost) { favorRecibo = r2(abonoRecibo - cost); avisoFavor = favorRecibo; }
        rem = 0;
      } else {
        abonoRecibo = r2(Math.min(cost, rem));
        rem = r2(rem - abonoRecibo);
      }
      // Guardar en moneda propia de la factura (igual que useCxc.js).
      // facturaDesdeRecibo es la inversa exacta de abonoEnRecibo → el restante
      // cierra en 0 aunque el abono exceda el saldo (saldo a favor).
      const abono   = facturaDesdeRecibo(f, abonoRecibo);
      const esUSDf  = (f.moneda || "CRC").toUpperCase() === "USD";
      const saldoF  = r2(Number(f.saldo));
      const favor   = favorRecibo > 0
        ? (esUSDf ? r4(abono - saldoF) : r2(abono - saldoF))
        : 0;
      return { ...f, abono, favor, checked: abonoRecibo > 0, comision_bancaria: 0, tratar_como_diferencial: false };
    });
    setFacturasCxP(next);
    if (avisoFavor > 0) {
      setFavorMsg(t("recibos.alertaFavor", {
        favor: `${form.moneda_pago === 'USD' ? '$' : '₡'}${fmt(avisoFavor)}`,
      }));
    }
  }

  // ── Saldos a favor marcados (≥2 habilita "Acumular/Fusionar") ─────────
  const negativasChecked = facturasCxP.filter((f) => f.checked && Number(f.saldo) < 0);
  const puedeFusionar = negativasChecked.length >= 2;

  // ── Pago 100% con saldo a favor: referencia autogenerada, igual que
  // soloSaldoFavor en useCxc.js/ReceiptDetails.js ─────────────────────────
  const negativaSeleccionada = facturasCxP.find((f) => f.checked && Number(f.abono) < 0);
  const montoNumRaw = Number(form.monto) || 0;
  const soloSaldoFavor = montoNumRaw === 0 && !!negativaSeleccionada;
  const referenciaAutoSaldo = soloSaldoFavor
    ? (negativaSeleccionada.concepto || negativaSeleccionada.factura_cxp?.concepto || "Aplicación saldo a favor")
    : null;

  function handleReset() {
    setForm({ ...FORM_INIT });
    setFacturasCxP([]);
    setErrors({});
    setApiErr("");
    setFavorMsg("");
    setPage(1);
  }

  function validate() {
    const e = {};
    const sel    = facturasCxP.filter(f => f.checked);
    const hayNeg = sel.some(f => Number(f.saldo) < 0);
    const hayPos = sel.some(f => Number(f.saldo) > 0);
    if (!form.cuenta_proveedor)                  e.cuenta_proveedor = t("cxp.proveedorReqMsg");
    if (!form.fecha_pago)                        e.fecha_pago       = t("cxp.fechaPagoReq");
    // Monto: normalmente > 0, pero se permite 0 cuando un saldo a favor (hayNeg)
    // cubre exactamente una factura pendiente (hayPos) — no entra efectivo del banco.
    // Igual que CxC: el crédito paga la deuda y el asiento cuadra por sí solo.
    const montoNum = Number(form.monto);
    if (montoNum < 0) {
      // El crédito seleccionado supera lo que se está pagando → no es válido.
      e.monto = t("cxp.creditoExcedePago");
    } else if (montoNum === 0 && !(hayNeg && hayPos)) {
      e.monto = t("cxp.montoReq");
    }
    if (!form.cuenta_bancaria)                   e.cuenta_bancaria  = t("cxp.cuentaBancariaReq");
    if (!soloSaldoFavor && !form.documento.trim()) e.documento       = t("cxp.referenciaObligatoria");
    if (sel.length === 0)                         e.facturas         = t("cxp.seleccionaFacturaCxP");
    // Un saldo a favor necesita una factura pendiente destino donde aplicarse
    else if (hayNeg && !hayPos)                   e.facturas         = t("cxp.saldoFavorNecesitaDestino");
    else if (Math.abs(restante) > 0.05)           e.facturas         = t("recibos.restanteError");
    if (form.cuenta_bancaria && form.monto) {
      const banco = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
      if (banco) {
        const bMoneda = (banco.moneda || 'CRC').toUpperCase();
        const tc = Number(form.tipo_cambio) || 1;
        const comisionVal = sel.reduce((s, f) => s + Number(f.comision_bancaria || 0), 0);
        const montoConComisionVal = Number(form.monto) + comisionVal;
        const montoEnBanco = bMoneda === form.moneda_pago
          ? montoConComisionVal
          : bMoneda === 'USD'
            ? montoConComisionVal / tc
            : montoConComisionVal * tc;
        if (montoEnBanco > Number(banco.saldo || 0)) {
          const saldoStr = bMoneda === 'USD' ? `$${fmt(banco.saldo||0)}` : `₡${fmt(banco.saldo||0)}`;
          e.cuenta_bancaria = `${t("cxp.saldoInsuficiente")} - disponible: ${saldoStr}`;
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr(""); setSuccessMsg("");
    if (!validate()) return;
    setSaving(true);
    try {
      const facturasToProcess = facturasCxP.filter(f => f.checked && Number(f.abono) !== 0);
      if (facturasToProcess.length === 0) {
        setApiErr(t("cxp.seleccionaFacturaCxP"));
        setSaving(false);
        return;
      }

      const isSingle = facturasToProcess.length === 1;
      const documentoFinal = soloSaldoFavor
        ? (referenciaAutoSaldo || "Aplicación saldo a favor")
        : form.documento.trim();

      let body;
      if (isSingle) {
        // Caso simple: un solo recibo con factura_cxp directa
        const cxp = facturasToProcess[0];
        body = {
          fecha_pago:              form.fecha_pago,
          // Con Diferencial Cambiario el banco desembolsa el monto completo y
          // el backend capa el saldo de la factura (registrar_pago) — igual que
          // en CxC. Sin diferencial, el monto es el abono aplicado (que ya
          // incluye el excedente que se vuelve saldo a favor).
          monto:                   cxp.tratar_como_diferencial
                                     ? r2(Number(form.monto) || 0)
                                     : r2(Number(abonoEnRecibo(cxp, cxp.abono))),
          metodo_pago:             form.metodo_pago,
          documento:               documentoFinal,
          descripcion:             form.descripcion.trim(),
          cuenta_bancaria:         form.cuenta_bancaria,
          factura_cxp:             cxp?.factura_cxp?.id ?? null,
          moneda_pago:             form.moneda_pago,
          tipo_cambio:             Number(form.tipo_cambio) || 1,
          tratar_como_diferencial: !!cxp.tratar_como_diferencial,
          comision_bancaria:       Number(cxp.comision_bancaria || 0),
        };
      } else {
        // Múltiples facturas → un único ReciboCxP con detalles, cada uno con
        // su propia comisión/diferencial (paridad por-factura con CxC).
        const detalles = facturasToProcess.map((cxp) => ({
          factura_cxp:             cxp?.factura_cxp?.id ?? null,
          monto:                   r2(Number(abonoEnRecibo(cxp, cxp.abono))),
          tratar_como_diferencial: !!cxp.tratar_como_diferencial,
          comision_bancaria:       Number(cxp.comision_bancaria || 0),
        }));
        body = {
          fecha_pago:      form.fecha_pago,
          monto:           Number(form.monto),
          metodo_pago:     form.metodo_pago,
          documento:       documentoFinal,
          descripcion:     form.descripcion.trim(),
          cuenta_bancaria: form.cuenta_bancaria,
          moneda_pago:     form.moneda_pago,
          tipo_cambio:     Number(form.tipo_cambio) || 1,
          detalles,
        };
      }

      const res = await apiFetch("/api/finance/reciboCxP/", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      const created = await res.json();

      setSuccessMsg(t("cxp.pagoRegistradoMsg", { numero: created.numero_recibo }));
      // Recargar facturas del proveedor para el próximo pago, igual que
      // reloadCxc() en CxC — mantiene el proveedor seleccionado.
      reloadFacturas();
      setForm(p => ({ ...p, monto: "", documento: "" }));
      setErrors({});
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  // Totales (para summary y validación bancaria), en la moneda del recibo.
  // El abono está en la moneda PROPIA de cada factura (igual que useCxc.js)
  // — convertir con abonoEnRecibo() antes de sumar (ese era el bug original:
  // usaba el SALDO completo de la factura en vez del abono aplicado).
  const facturasChecked   = facturasCxP.filter(f => f.checked);
  const sumAbonosCheck    = r2(facturasChecked.reduce((s, f) => s + costoAbono(f), 0));
  const sumFavorCheck     = r2(facturasChecked.reduce((s, f) => {
    const facMon = (f.moneda || "CRC").toUpperCase();
    const favor  = Number(f.favor || 0);
    return s + (facMon === form.moneda_pago ? r2(favor) : convARecibo(favor));
  }, 0));
  // Una factura marcada como Diferencial Cambiario absorbe TODO el presupuesto
  // restante (igual que hasDiferencial en useCxc.js) → restante = 0.
  const hayDiferencial    = facturasChecked.some(f => f.tratar_como_diferencial && Number(f.abono) > 0);
  const restante          = hayDiferencial
    ? 0
    : r2((Number(form.monto) || 0) - sumAbonosCheck);

  // Facturas seleccionadas en moneda distinta al recibo → línea "incluye..."
  // (mismo propósito que la línea equivalente de CxcSummary.js)
  const seleccionadasCruzadas = facturasChecked.filter(f => (f.moneda || 'CRC') !== form.moneda_pago);
  const totalCruzadoRecibo = seleccionadasCruzadas.reduce((s, f) => s + Math.abs(Number(abonoEnRecibo(f, f.abono || 0))), 0);

  const totalPages = Math.ceil(facturasCxP.length / PAGE_SIZE);
  const visibles   = facturasCxP.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const errorBorder = (field) => errors[field] ? { borderColor:"#ef4444" } : undefined;
  const montoNum   = Number(form.monto) || 0;
  const comisionTotalSel = facturasCxP.filter(f => f.checked).reduce((s, f) => s + Number(f.comision_bancaria || 0), 0);
  const montoConComision = montoNum + comisionTotalSel;
  const bancoDato  = cuentasBancarias.find(b => b.id === form.cuenta_bancaria);
  const bancoMoneda = (bancoDato?.moneda || 'CRC').toUpperCase();
  // Comparar monto (+ comisión, también sale del banco) vs saldo bancario
  const montoParaValidar = bancoMoneda === form.moneda_pago
    ? montoConComision
    : bancoMoneda === 'USD'
      ? montoConComision / (Number(form.tipo_cambio) || 1)   // CRC pago → USD banco
      : montoConComision * (Number(form.tipo_cambio) || 1);  // USD pago → CRC banco
  const saldoInsuficiente = bancoDato && montoNum > 0 && montoParaValidar > Number(bancoDato.saldo || 0);

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <Link to="/finanzas/cxp/recibos">
          <span className={styles.inactive}> {t("cxp.recibos")} /</span>
        </Link>
        <span className={styles.active}> {t("common.agregar")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("cxp.registrarPagoProveedor")}</h2>

      {/* Éxito */}
      {successMsg && (
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>{successMsg}
          </span>
          <button onClick={() => setSuccessMsg("")}
            style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer" }}>✕</button>
        </div>
      )}

      {/* Error API */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      {/* ── Toggle Pago sin identificar ──────────────────────────────── */}
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
        <button type="button"
          onClick={() => { setSinIdentificarMode(v => !v); setSinIdentificarError(""); }}
          style={{
            padding: "6px 16px",
            background: sinIdentificarMode ? "rgba(249,115,22,0.15)" : "var(--hover-bg)",
            border: `1px solid ${sinIdentificarMode ? "rgba(249,115,22,0.5)" : "var(--border-card)"}`,
            borderRadius: 7, color: sinIdentificarMode ? "#f97316" : "var(--text-secondary)",
            fontSize: "0.83rem", fontWeight: 600, cursor: "pointer",
          }}>
          <i className={`fa-solid fa-${sinIdentificarMode ? "toggle-on" : "toggle-off"}`} style={{ marginRight: 6 }}/>
          {t("cxp.pagoSinIdentificarToggle")}
        </button>
        {sinIdentificarMode && (
          <span style={{ color: "#f97316", fontSize: "0.78rem" }}>
            {t("cxp.pagosSinIdentificar")}
          </span>
        )}
      </div>

      {sinIdentificarMode ? (
        <PagoSinIdentificarFormCxP
          form={sinIdentificarForm}
          setForm={setSinIdentificarForm}
          error={sinIdentificarError}
          setError={setSinIdentificarError}
          saving={sinIdentificarSaving}
          setSaving={setSinIdentificarSaving}
          onCreated={(r) => {
            setSinIdentificarMode(false);
            setSinIdentificarForm({
              monto: "", fecha_pago: "", metodo_pago: "TRANSFERENCIA",
              documento: "", descripcion: "", cuenta_bancaria: "",
              tipo_cambio: "", moneda_pago: "CRC", cuenta_proveedor: "",
            });
            setSuccessMsg(t("cxp.pagoRegistradoMsg", { numero: r.numero_recibo }));
          }}
        />
      ) : (
      <form onSubmit={handleSubmit}>
        <div className={styles["receipt-container"]}>

          {/* ── Izquierda: Detalles del Pago ── */}
          <div className={styles["receipt-details"]}>
            <div className={styles["receipt-details-header"]}>
              <h1>
                <i className={`fa-solid fa-money-bill-transfer ${styles["icon-receipt-details"]}`}/>
                {t("cxp.detallesPago")}
              </h1>
            </div>
            <div className={styles["receipt-details-body"]} style={{ marginTop:14 }}>

              {/* ── Fila 1: Fecha + Proveedor (igual orden que CxC: Fecha + Filial) ── */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">
                      {t("cxp.fechaPago")} <span style={{ color:"#f87171" }}>*</span>
                    </label>
                    <input className="form-control" type="date" value={form.fecha_pago}
                      onChange={e => set("fecha_pago", e.target.value)}
                      style={errorBorder("fecha_pago")}/>
                    <FieldError msg={errors.fecha_pago}/>
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">
                      {t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span>
                    </label>
                    <SearchSelect
                      icon="fa-building"
                      options={cuentasProveedor.map(c => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
                      value={form.cuenta_proveedor}
                      onChange={handleProveedor}
                      placeholder={t("cxp.seleccionaProveedor")}
                      error={errors.cuenta_proveedor}
                    />
                  </div>
                </div>
              </div>

              {/* ── Fila 2: Medio de pago + Cuenta bancaria (igual orden que CxC) ── */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">{t("cxp.medioPago")}</label>
                    <select className="form-select" value={form.metodo_pago}
                      onChange={e => set("metodo_pago", e.target.value)}>
                      <option value="TRANSFERENCIA">{t("cxp.transferencia")}</option>
                      <option value="EFECTIVO">{t("cxp.efectivo")}</option>
                      <option value="CHEQUE">{t("cxp.cheque")}</option>
                      <option value="TARJETA">{t("cxp.tarjeta")}</option>
                    </select>
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label">
                      {t("cxp.cuentaBancaria")} <span style={{ color:"#f87171" }}>*</span>
                    </label>
                    {cuentasBancarias.length === 0 ? (
                      <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                        borderRadius:6, padding:"8px 12px", color:"#f87171", fontSize:"0.82rem",
                        display:"flex", alignItems:"center", gap:7 }}>
                        <i className="fa-solid fa-triangle-exclamation"/>
                        {t("cxp.sinCuentasBancariasSettings")}
                      </div>
                    ) : (
                      <>
                        <select className="form-select"
                          value={form.cuenta_bancaria ?? ""}
                          onChange={handleBancoChange}
                          style={errorBorder("cuenta_bancaria")}>
                          {cuentasBancarias.map(cb => {
                            const cbMoneda = (cb.moneda || 'CRC').toUpperCase();
                            return (
                              <option key={cb.id} value={cb.id}>
                                {cb.banco} - {cb.numero_cuenta}{multimoneda ? (cbMoneda === 'USD' ? ' (USD)' : ' (₡)') : ''}
                              </option>
                            );
                          })}
                        </select>
                        <FieldError msg={errors.cuenta_bancaria}/>
                        {/* Moneda de la cuenta — solo si es USD, como referencia rápida (igual que CxC) */}
                        {multimoneda && bancoDato && bancoMoneda === 'USD' && (
                          <div style={{ fontSize:"0.74rem", color:"#60a5fa", marginTop:3, display:"flex", alignItems:"center", gap:4 }}>
                            <i className="fa-solid fa-circle-info" style={{ fontSize:"0.65rem" }}/>
                            {t("cxp.cuentaEnDolaresHint")}
                          </div>
                        )}
                        {saldoInsuficiente && (
                          <div style={{ marginTop:4, padding:"5px 10px",
                            background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
                            borderRadius:6, color:"#f87171", fontSize:"0.8rem",
                            display:"flex", alignItems:"center", gap:6 }}>
                            <i className="fa-solid fa-triangle-exclamation"/>
                            {t("cxp.saldoInsuficiente")} - disponible: {bancoMoneda === 'USD' ? `$${fmt(bancoDato?.saldo||0)}` : `₡${fmt(bancoDato?.saldo||0)}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* ── TC Compra — card, mismo diseño exacto que ReceiptDetails.js de CxC ── */}
              {multimoneda && (
                <div style={{
                  marginTop: 8, border: "1px solid var(--border-card)", borderRadius: 10,
                  overflow: "hidden", background: "rgba(255,255,255,0.02)",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "9px 14px",
                    borderBottom: "1px solid var(--border-card)",
                    background: bancoMoneda === "USD" ? "rgba(96,165,250,0.05)" : "rgba(249,115,22,0.04)",
                  }}>
                    <i className="fa-solid fa-arrow-right-arrow-left"
                       style={{ color: bancoMoneda === "USD" ? "#60a5fa" : "#f97316", fontSize: "0.7rem" }}/>
                    <span style={{ fontSize: "0.67rem", fontWeight: 700, color: "var(--text-muted)",
                      letterSpacing: "0.6px", textTransform: "uppercase" }}>
                      {t("cxp.tipoDeCambio")}
                    </span>
                    {bancoMoneda === "USD" && (
                      <span style={{
                        marginLeft: "auto", fontSize: "0.62rem", fontWeight: 700,
                        background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.3)",
                        borderRadius: 4, padding: "1px 7px", color: "#60a5fa", letterSpacing: "0.3px",
                      }}>{t("cxp.bancoUSD")}</span>
                    )}
                  </div>
                  <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                        {t("cxp.tcCompra")}:
                      </span>
                      {loadingTC
                        ? <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa", fontSize: "0.7rem" }}/>
                        : <span style={{
                            fontSize: "0.64rem", fontWeight: 700,
                            background: `${(tipoCambio?.fuente === 'MANUAL' ? '#fb923c' : tipoCambio?.fuente === 'FALLBACK' ? '#f87171' : '#4ade80')}18`,
                            border: `1px solid ${(tipoCambio?.fuente === 'MANUAL' ? '#fb923c' : tipoCambio?.fuente === 'FALLBACK' ? '#f87171' : '#4ade80')}45`,
                            borderRadius: 4, padding: "2px 7px",
                            color: tipoCambio?.fuente === 'MANUAL' ? '#fb923c' : tipoCambio?.fuente === 'FALLBACK' ? '#f87171' : '#4ade80',
                            letterSpacing: "0.3px",
                          }}>{tipoCambio?.fuente === 'MANUAL' ? t("cxp.tcManual") : tipoCambio?.fuente === 'FALLBACK' ? t("cxp.tcSinDatos") : 'BCCR'}</span>
                      }
                      <div style={{
                        display: "flex", alignItems: "center", gap: 4,
                        background: "rgba(0,0,0,0.2)", border: "1px solid var(--border-card)",
                        borderRadius: 7, padding: "4px 10px 4px 9px",
                      }}>
                        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: 600, lineHeight: 1 }}>₡</span>
                        <DecimalInput value={form.tipo_cambio}
                          onChange={(e) => set("tipo_cambio", parseFloat(e.target.value) || 1)}
                          style={{ width: 82, background: "transparent", border: "none", color: "var(--text-primary)",
                            fontSize: "0.88rem", fontFamily: "monospace", fontWeight: 600, outline: "none", padding: 0 }}/>
                        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>/ USD</span>
                      </div>
                      {bancoMoneda === "CRC" && (
                        <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                          ({t("cxp.referenciaFacturasUSD")})
                        </span>
                      )}
                    </div>
                    {montoNum > 0 && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                        background: "rgba(0,0,0,0.15)", borderRadius: 7, padding: "8px 12px",
                      }}>
                        <i className="fa-solid fa-arrow-right-arrow-left"
                           style={{ color: "var(--text-muted)", fontSize: "0.62rem", flexShrink: 0 }}/>
                        {bancoMoneda === "USD" ? (
                          <>
                            <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                              ${fmt(montoNum)}
                            </span>
                            <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>{t("cxp.usdEquivaleA")}</span>
                            <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                              ₡{fmt(montoNum * (Number(form.tipo_cambio) || 1))}
                            </span>
                            <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>{t("cxp.alTC", { tc: (Number(form.tipo_cambio)||1).toFixed(2) })}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                              ₡{fmt(montoNum)}
                            </span>
                            <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>{t("cxp.equivaleA")}</span>
                            <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                              ${(montoNum / (Number(form.tipo_cambio) || 1)).toFixed(4)} USD
                            </span>
                            <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>{t("cxp.alTC", { tc: (Number(form.tipo_cambio)||1).toFixed(2) })}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Fila 3: Monto — mismo diseño exacto que ReceiptDetails.js
                  (caja de símbolo pegada al input + botón Distribuir) ── */}
              <div className="row" style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label className="form-label">
                    {form.moneda_pago === 'USD' ? '$' : '₡'} {t("cxp.montoTotal")}{" "}
                    <span style={{ color:"#f87171" }}>*</span>
                    {multimoneda && form.moneda_pago === 'USD' && (
                      <span style={{ fontWeight: 400, fontSize: "0.78rem", color: "#60a5fa", marginLeft: 6 }}>
                        ({t("cxp.enDolares")})
                      </span>
                    )}
                  </label>
                  <div className="d-flex" style={{ flexDirection: "column" }}>
                    <div className="d-flex">
                      <div style={{
                        display: "flex", alignItems: "center",
                        background: "var(--bg-input)", border: "1px solid var(--border-card)",
                        borderRadius: "7px 0 0 7px", padding: "0 10px",
                        color: form.moneda_pago === 'USD' ? "#60a5fa" : "var(--text-secondary)",
                        fontWeight: 700, fontSize: "0.9rem",
                      }}>
                        {form.moneda_pago === 'USD' ? '$' : '₡'}
                      </div>
                      <input className="form-control" type="number" min="0" step="0.01"
                        placeholder="0.00" value={form.monto}
                        onChange={e => set("monto", e.target.value)}
                        style={{ ...errorBorder("monto"), borderRadius: "0", borderLeft: "none" }}/>
                      <button type="button" className="btn btn-primary" onClick={distribute}
                        disabled={facturasCxP.length === 0}>
                        {t("recibos.distribuir")}
                      </button>
                    </div>
                    <FieldError msg={errors.monto}/>
                  </div>
                  {favorMsg && (
                    <div style={{ marginTop:6, padding:"7px 12px", borderRadius:6,
                      background:"rgba(251,146,60,0.1)", border:"1px solid rgba(251,146,60,0.3)",
                      color:"#fb923c", fontSize:"0.8rem" }}>
                      <i className="fa-solid fa-piggy-bank" style={{ marginRight:6 }}/>{favorMsg}
                    </div>
                  )}
                </div>
              </div>

              {/* Referencia — autogenerada y deshabilitada si el pago es 100%
                  aplicación de saldo a favor (igual que soloSaldoFavor en CxC) */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">
                    {t("cxp.referenciaBancaria")}{" "}
                    {soloSaldoFavor
                      ? <span style={{ color:"#f97316", fontSize:"0.78rem", fontWeight:400 }}>({t("cxp.referenciaAutoSaldo")})</span>
                      : <span style={{ color:"#f87171" }}>*</span>}
                  </label>
                  <input className="form-control" type="text"
                    placeholder={t("cxp.referenciaPlaceholder")}
                    value={soloSaldoFavor ? (referenciaAutoSaldo || "") : form.documento}
                    onChange={e => { if (!soloSaldoFavor) set("documento", e.target.value); }}
                    disabled={soloSaldoFavor}
                    style={{
                      ...errorBorder("documento"),
                      ...(soloSaldoFavor
                        ? { background:"rgba(249,115,22,0.06)", borderColor:"rgba(249,115,22,0.3)", color:"#f97316", cursor:"not-allowed" }
                        : {}),
                    }}/>
                  <FieldError msg={errors.documento}/>
                </div>
              </div>

              {/* Descripción (opcional) */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">
                    {t("cxp.descripcionLabel")}{" "}
                    <span style={{ color:"var(--text-muted)", fontWeight:400 }}>
                      ({t("common.opcional")})
                    </span>
                  </label>
                  <input className="form-control" type="text"
                    placeholder={t("cxp.descripcionPlaceholder")}
                    value={form.descripcion}
                    onChange={e => set("descripcion", e.target.value)}/>
                  <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                    {t("cxp.descripcionHint")}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ── Derecha: Facturas Pendientes ── */}
          <div className={styles["cxc-details"]}>
            <div className={styles["cxc-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice ${styles["icon-cxc-details"]}`}/>
                {t("cxp.facturasPendientes")}
              </h1>
            </div>

            <div className={styles["receipt-details-body"]}>
              {!form.cuenta_proveedor ? (
                <div style={{ padding:"40px 16px", textAlign:"center", color:"var(--text-muted)" }}>
                  <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block",
                    marginBottom:12, color:"var(--text-muted)" }}/>
                  {t("cxp.sinProveedor")}
                </div>
              ) : loadingFac ? (
                <div style={{ padding:40, textAlign:"center", color:"var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ color:"#f97316", fontSize:"1.3rem" }}/>
                </div>
              ) : facturasCxP.length === 0 ? (
                <div style={{ padding:32, textAlign:"center", color:"var(--text-muted)", fontSize:"0.87rem" }}>
                  <i className="fa-solid fa-check-circle" style={{ color:"#4ade80",
                    fontSize:"1.4rem", display:"block", marginBottom:8 }}/>
                  {t("cxp.sinFacturasCxP")}
                </div>
              ) : (
                <>
                  {/* Botón Acumular Saldos — visible con ≥2 saldos a favor marcados */}
                  {puedeFusionar && (
                    <div style={{ marginBottom: 8 }}>
                      <button type="button" onClick={() => setFusionOpen(true)}
                        style={{ padding:"6px 12px", background:"rgba(74,222,128,0.12)",
                          border:"1px solid rgba(74,222,128,0.4)", borderRadius:6, color:"#4ade80",
                          fontWeight:600, fontSize:"0.8rem", cursor:"pointer", whiteSpace:"nowrap" }}>
                        <i className="fa-solid fa-layer-group" style={{ marginRight:6 }}/>
                        {t("recibos.acumularSaldos")}
                      </button>
                    </div>
                  )}

                  {/* Tabla de facturas — mismas clases CSS que CxC (invoice-table) */}
                  <div className={styles["table-responsive"]}>
                    <div className={styles["invoice-table"]}>
                      <table>
                        <thead>
                          <tr>
                            <th>
                              <input type="checkbox"
                                checked={facturasCxP.some(f => f.checked)}
                                onChange={(e) => {
                                  if (e.target.checked) distribute();
                                  else setFacturasCxP(prev => prev.map(f => ({ ...f, abono: 0, favor: 0, checked: false, comision_bancaria: 0, tratar_como_diferencial: false })));
                                  setPage(1);
                                }}/>
                            </th>
                            <th>{t("cxp.numFactura")}</th>
                            <th>{t("cxp.concepto")}</th>
                            <th>{t("cxp.saldo")}</th>
                            <th style={{ paddingRight: "20px" }}>{t("cxp.abono")}</th>
                            <th style={{ paddingLeft: 6 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibles.map(cxp => {
                            const fac = cxp.factura_cxp;
                            const esFavor = Number(cxp.saldo) < 0;
                            const esUSD = (cxp.moneda || 'CRC') === 'USD';
                            const esCruzada = (cxp.moneda || 'CRC') !== form.moneda_pago;
                            return (
                              <tr key={cxp.id}>
                                <td>
                                  <input type="checkbox" checked={cxp.checked}
                                    onChange={() => toggleFactura(cxp.id)}/>
                                </td>
                                <td>
                                  {fac?.numero_doc_proveedor || fac?.numero_factura || "-"}
                                  {fac?.fecha_emision && (
                                    <span style={{
                                      display: "block", fontSize: "0.7rem",
                                      color: "var(--text-muted)", marginTop: 1,
                                      whiteSpace: "nowrap", fontWeight: 400,
                                    }}>
                                      {fmtDate(fac.fecha_emision)}
                                    </span>
                                  )}
                                  <div style={{ fontSize: "0.68rem", marginTop: 2 }}>
                                    <span style={{
                                      display: "inline-block", padding: "1px 6px", borderRadius: 4,
                                      fontWeight: 700, fontSize: "0.65rem",
                                      background: esUSD ? "rgba(96,165,250,0.15)" : "rgba(74,222,128,0.12)",
                                      color: esUSD ? "#60a5fa" : "#4ade80",
                                    }}>
                                      {cxp.moneda || 'CRC'}
                                    </span>
                                    {esFavor && (
                                      <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                                        fontSize: "0.65rem", fontWeight: 700,
                                        background: "rgba(74,222,128,0.15)", color: "#4ade80" }}>
                                        {t("cxp.aFavor")}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                                  {cxp.concepto || fac?.concepto || "-"}
                                </td>
                                <td className={styles.saldo}>
                                  {esUSD ? (
                                    <>
                                      <span style={{ color: "#60a5fa" }}>${Number(cxp.saldo).toFixed(2)}</span>
                                      {cxp.saldo_colones != null && (
                                        <span style={{ display: "block", fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 1 }}>
                                          ≈ ₡{fmt(cxp.saldo_colones)}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      ₡{fmt(cxp.saldo)}
                                      {form.moneda_pago === 'USD' && (
                                        <span style={{ display: "block", fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 1 }}>
                                          ≈ ${(Math.abs(Number(cxp.saldo)) / (Number(form.tipo_cambio) || 1)).toFixed(2)}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </td>
                                <td>
                                  {/* Prefijo de moneda propia — el abono se edita en la
                                      moneda ORIGINAL de la factura, igual que InvoiceCxc.js */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: "0.78rem", fontWeight: 600,
                                      color: esUSD ? "#60a5fa" : "var(--text-muted)" }}>
                                      {esUSD ? "$" : "₡"}
                                    </span>
                                    <AbonoInput
                                      className={styles["input-abono"]}
                                      value={cxp.checked ? Number(cxp.abono || 0) : 0}
                                      disabled={!cxp.checked}
                                      esUSD={esUSD}
                                      onChange={(num) => updateAbono(cxp.id, num)}/>
                                  </div>
                                  {cxp.favor > 0 && (
                                    <div style={{ fontSize: "0.68rem", color: "#fb923c", marginTop: 2 }}>
                                      + {esUSD ? '$' : '₡'}{fmt(cxp.favor)} {t("cxp.aFavor")}
                                    </div>
                                  )}
                                  {Number(cxp.comision_bancaria) > 0 && (
                                    <div style={{ fontSize: "0.68rem", color: "#fcd34d", marginTop: 2 }}>
                                      <i className="fa-solid fa-credit-card" style={{ marginRight:3 }}/>
                                      + comisión {form.moneda_pago === 'USD' ? '$' : '₡'}{fmt(cxp.comision_bancaria)}
                                    </div>
                                  )}
                                </td>
                                {/* Botones por factura: Comisión (cualquier método de pago:
                                    el banco cobra comisión en tarjeta, pero también en
                                    transferencias internacionales y cheques) y Diferencial
                                    (solo cuando el pago y la factura están en monedas
                                    distintas). Antes compartían un if/else, así que quitar la
                                    condición de tarjeta habría dejado sin botón al diferencial. */}
                                <td style={{ paddingLeft: 6, verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                  {cxp.checked ? (
                                    <button type="button"
                                      disabled={!(Number(cxp.comision_bancaria) > 0) && (r2(Number(cxp.saldo)) - Number(cxp.abono || 0)) <= 0.005}
                                      title={Number(cxp.comision_bancaria) > 0
                                        ? "Diferencia tratada como Comisión Bancaria - click para revertir"
                                        : "Tratar diferencia como Comisión Bancaria (retenida por el banco)"}
                                      onClick={() => toggleComisionBancaria(cxp.id)}
                                      style={{
                                        padding: "2px 8px", fontSize: "0.72rem", fontWeight: 600, borderRadius: 4, cursor: "pointer",
                                        border: Number(cxp.comision_bancaria) > 0 ? "1px solid rgba(250,204,21,0.6)" : "1px solid rgba(148,163,184,0.3)",
                                        background: Number(cxp.comision_bancaria) > 0 ? "rgba(250,204,21,0.12)" : "transparent",
                                        color: Number(cxp.comision_bancaria) > 0 ? "#fcd34d" : "var(--text-muted)",
                                        whiteSpace: "nowrap", marginRight: 4,
                                      }}>
                                      {Number(cxp.comision_bancaria) > 0
                                        ? <><i className="fa-solid fa-rotate-left" style={{ marginRight:4 }}/>Comisión ✓</>
                                        : <><i className="fa-solid fa-credit-card" style={{ marginRight:4 }}/>→ Comisión</>}
                                    </button>
                                  ) : null}
                                  {cxp.checked && esCruzada ? (
                                    <button type="button"
                                      title={cxp.tratar_como_diferencial
                                        ? "Exceso tratado como Diferencial Cambiario - click para revertir"
                                        : "Tratar exceso como Diferencial Cambiario en vez de Saldo a Favor"}
                                      onClick={() => toggleDiferencial(cxp.id)}
                                      style={{
                                        padding: "2px 8px", fontSize: "0.72rem", fontWeight: 600, borderRadius: 4, cursor: "pointer",
                                        border: cxp.tratar_como_diferencial ? "1px solid rgba(250,204,21,0.6)" : "1px solid rgba(148,163,184,0.3)",
                                        background: cxp.tratar_como_diferencial ? "rgba(250,204,21,0.12)" : "transparent",
                                        color: cxp.tratar_como_diferencial ? "#fcd34d" : "var(--text-muted)",
                                        whiteSpace: "nowrap",
                                      }}>
                                      {cxp.tratar_como_diferencial
                                        ? <><i className="fa-solid fa-rotate-left" style={{ marginRight:4 }}/>Diferencial ✓</>
                                        : <><i className="fa-solid fa-arrows-rotate" style={{ marginRight:4 }}/>→ Diferencial</>}
                                    </button>
                                  ) : null}
                                  {!cxp.checked && <span style={{ display:"inline-block", width:2 }}/>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className={styles["footer-info"]}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 14px", minWidth: 720 }}>
                        <span>{facturasCxP.length} {t("cxp.facturasPendientesSufijo")}</span>
                        {totalPages > 1 && (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <button type="button" disabled={page <= 1}
                              onClick={() => setPage(p => p - 1)}
                              style={{ padding: "3px 10px", fontSize: 12, background: "var(--hover-bg)",
                                color: "var(--text-secondary)", border: "1px solid var(--border-card)",
                                borderRadius: 4, cursor: page <= 1 ? "not-allowed" : "pointer" }}>
                              {t("common.anteriorCorto")}
                            </button>
                            <span style={{ fontSize: 12 }}>{page} / {totalPages}</span>
                            <button type="button" disabled={page >= totalPages}
                              onClick={() => setPage(p => p + 1)}
                              style={{ padding: "3px 10px", fontSize: 12, background: "var(--hover-bg)",
                                color: "var(--text-secondary)", border: "1px solid var(--border-card)",
                                borderRadius: 4, cursor: page >= totalPages ? "not-allowed" : "pointer" }}>
                              {t("common.siguienteCorto")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resumen */}
                  <div className={styles["cxc-details-summary"]}>
                    <div className={styles["cxc-summary-relevant"]}>
                      <h2>{t("cxp.resumenRapido")}</h2>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>{t("cxp.totalSeleccionado")}</span>
                        <span style={{ color: form.moneda_pago === 'USD' ? "#60a5fa" : undefined }}>
                          {form.moneda_pago === 'USD' ? '$' : '₡'} {fmt(sumAbonosCheck)}
                        </span>
                      </div>
                      {sumFavorCheck > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{t("cxp.totalSaldoFavor")}</span>
                          <span style={{ color: "#fb923c" }}>
                            {form.moneda_pago === 'USD' ? '$' : '₡'} {fmt(sumFavorCheck)}
                          </span>
                        </div>
                      )}
                      {comisionTotalSel > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{t("cxp.comisionBancariaTarjeta")}</span>
                          <span style={{ color: "#fcd34d" }}>
                            {form.moneda_pago === 'USD' ? '$' : '₡'} {fmt(comisionTotalSel)}
                          </span>
                        </div>
                      )}
                      {seleccionadasCruzadas.length > 0 && (
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4 }}>
                          {t("cxp.incluyeCruzado", {
                            n: seleccionadasCruzadas.length,
                            moneda: seleccionadasCruzadas[0].moneda,
                            simbolo: form.moneda_pago === 'USD' ? '$' : '₡',
                            monto: fmt(totalCruzadoRecibo),
                          })}
                        </div>
                      )}
                    </div>
                    <div className={styles["cxc-summary-important"]}>
                      <h2>
                        {restante < 0 ? t("cxp.saldoAFavorLabel")
                          : restante > 0 ? t("cxp.totalRestanteLabel")
                          : t("cxp.pagadoCompletoLabel")}
                      </h2>
                      <span style={{
                        color: restante < 0 ? "#4ade80" : restante > 0 ? "#f97316" : "#4ade80",
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {form.moneda_pago === 'USD' ? '$' : '₡'} {fmt(Math.abs(restante))}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={styles["receipt-footer"]}>
            {/* Resumen de errores de validación — sin esto el botón "Registrar
                Pago" fallaba en silencio (errors.facturas nunca se mostraba)
                igual que el bloque de errores de ReceiptFooter.js en CxC. */}
            {Object.keys(errors).length > 0 && (
              <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
                borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, color:"#f87171",
                  fontWeight:600, marginBottom: Object.keys(errors).length > 1 ? 6 : 0 }}>
                  <i className="fa-solid fa-triangle-exclamation"/>
                  {Object.keys(errors).length === 1
                    ? Object.values(errors)[0]
                    : `${t("common.hay")} ${Object.keys(errors).length} ${t("cxp.camposConError")}`}
                </div>
                {Object.keys(errors).length > 1 && (
                  <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
                    {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className={styles.actions}>
              <button type="button" onClick={handleReset}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}>
                <i className="fa-solid fa-broom" style={{ marginRight:6 }}/>
                {t("cxp.limpiarFormulario")}
              </button>
              <Link to="/finanzas/cxp/recibos"
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
                style={{ textDecoration:"none" }}>
                {t("common.cancelar")}
              </Link>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                  : <><i className="fa-solid fa-lock"/> {t("cxp.registrarPagoBtn")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
      )}

      {fusionOpen && (
        <AcumularSaldosDialogCxP
          negativas={negativasChecked}
          onClose={() => setFusionOpen(false)}
          onDone={() => { setFusionOpen(false); reloadFacturas(); }}
        />
      )}
    </>
  );
}
